/**
 * Живой звонок нельзя потерять молча.
 *
 * Комната живёт только на своей странице: уход с неё внутри приложения
 * (переход по ссылке, «Назад» в браузере) размонтирует комнату — соединение
 * закрывается, камера гаснет, запись обрывается. Прогон 28.09.2026: «Назад»
 * посреди записи без единого вопроса закрыл звонок за три секунды, кандидат
 * увидел «Собеседник вышел», запись ушла в «неполную».
 *
 * Правило одно: пока идёт звонок, любой уход со страницы либо открывает новую
 * вкладку (уведомления), либо спрашивает (выход, «Назад»). Как в Meet: звонок
 * живёт в своей вкладке, а метка «●» в названии вкладки не даёт её потерять в
 * ряду.
 */
import { isVideoLivePath } from "@/lib/videoRoomPath";

export type LiveCallActivity = "idle" | "recording" | "saving";

/** Название вкладки на время звонка: «● Интервью · Анна Петрова». */
export function liveCallTitle(peerName: string): string {
  const name = peerName.trim();
  return name ? `● Интервью · ${name}` : "● Интервью";
}

/**
 * Что спросить перед уходом из комнаты. null — спрашивать не о чем: звонка
 * нет и запись не сохраняется. Рекрутёру подсказываем, как заглянуть в CRM,
 * не бросая встречу; кандидату про CRM говорить незачем.
 */
export function leaveCallConfirmText(input: {
  mode: "recruiter" | "candidate";
  activity: LiveCallActivity;
  live: boolean;
}): string | null {
  const recruiter = input.mode === "recruiter";
  const crmHint =
    " Чтобы заглянуть в CRM, откройте её в новой вкладке — встреча останется здесь.";
  if (input.activity === "recording") {
    return recruiter
      ? `Идёт запись. Уйти со страницы? Встреча прервётся, сохранение всей записи не гарантируется.${crmHint}`
      : "Идёт запись. Уйти со страницы? Интервью прервётся, запись вашего голоса может не сохраниться.";
  }
  if (input.activity === "saving") {
    return recruiter
      ? "Запись ещё сохраняется. Уйти со страницы? Несохранённая запись будет потеряна."
      : "Запись ещё отправляется. Уйти со страницы? Неотправленная запись будет потеряна.";
  }
  if (input.live) {
    return recruiter
      ? `Выйти из интервью? Звонок прервётся.${crmHint}`
      : "Выйти из интервью? Звонок прервётся.";
  }
  return null;
}

/**
 * Адрес сменился, пока звонок идёт («Назад», «Вперёд», переход по ссылке).
 *
 * `ask` — спросить человека: адрес другой, и после ухода роутер снимет
 * комнату. `pass` — адрес тот же (сменились только параметры или это наш же
 * возврат на адрес комнаты), комнату он не снимет.
 */
export function leaveNavigationDecision(input: {
  roomPath: string;
  nextPath: string;
}): "ask" | "pass" {
  return input.nextPath === input.roomPath ? "pass" : "ask";
}

/**
 * Куда вести кнопку «Открыть» всплывающего уведомления. На странице живой
 * встречи — в новую вкладку: переход в этой же вкладке закрыл бы звонок.
 */
export function popupLinkTarget(pathname: string): "new-tab" | "same-tab" {
  return isVideoLivePath(pathname) ? "new-tab" : "same-tab";
}
